import type { Recommendation } from '../../api/types'

/**
 * Should the panel show the user's own decision alongside the recommendation?
 *
 * Only when it says something the recommendation does not. Agreement is already
 * visible in the decision control at the top of the panel, so repeating it below
 * adds a line that is never news; disagreement is the one case a reader needs
 * pointed out, and it earns its prominence by being rare.
 *
 * Named verdictRules, not verdict: a `verdict.ts` beside `Verdict.tsx` is the
 * same file on a case-insensitive filesystem, so `import … from './Verdict'`
 * silently resolved here and the panel died with "does not provide an export
 * named 'Verdict'". Typecheck, lint and the unit tests all passed — only opening
 * the page caught it.
 *
 * Declared as a type guard, not a plain boolean: when it returns true the
 * decision is necessarily non-null, so the caller can label it without a second
 * null check that would imply a case which cannot happen.
 *
 * Kept as a pure function rather than an inline comparison so the rule is
 * testable without a DOM (docs/16 section 13.3), and so "no duplicate when they
 * agree" is a stated guarantee rather than an implementation detail that a
 * future edit could quietly invert.
 */
export function decisionAddsInformation(
  decision: Recommendation | null,
  recommendation: Recommendation | null,
): decision is Recommendation {
  if (decision === null) return false
  return decision !== recommendation
}
